import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoadPanel } from "devextreme-react";
import notify from "devextreme/ui/notify";
import { Ticket, getTickets, isClosedStatus, TICKET_STATUS_DEFS, TicketStatus, TicketPriority } from "../../../api/support";
import { STATUS_BADGE, STATUS_TEXT_KEYS, PRIORITY_BADGE, PRIORITY_TEXT_KEYS, PRODUCT_TEXT } from "../messages/ticket-constants";

type Slice = { label: string; value: number; color: string };

// Ticket analizi raporu: Ticketleri durum/oncelik/tip/urun kirilimlarinda ozetler (6500).
// Backend bos donerse ornek (mock) dagilim gosterilir.
export default function TicketAnalysisPage() {
    const { t } = useTranslation();
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        getTickets()
            .then(setTickets)
            .catch(() => notify(t("ticketsLoadError"), "error", 2000))
            .finally(() => setLoading(false));
    }, [t]);

    const data = useMemo(() => {
        const total = tickets.length;
        if (total === 0) return MOCK_ANALYTICS(t);

        let open = 0, closed = 0;
        const statusCount = new Map<number, number>();
        const prioCount = new Map<number, number>();
        const typeCount = new Map<string, number>();
        const productCount = new Map<string, number>();

        for (const tk of tickets) {
            if (isClosedStatus(tk.status)) closed++; else open++;
            statusCount.set(tk.status, (statusCount.get(tk.status) ?? 0) + 1);
            if (tk.priority) prioCount.set(tk.priority, (prioCount.get(tk.priority) ?? 0) + 1);
            const typeName = tk.type_name || "—";
            typeCount.set(typeName, (typeCount.get(typeName) ?? 0) + 1);
            const prod = PRODUCT_TEXT[tk.product_code as number] || `#${tk.product_code ?? "—"}`;
            productCount.set(prod, (productCount.get(prod) ?? 0) + 1);
        }

        const byStatus: Slice[] = TICKET_STATUS_DEFS
            .filter(d => (statusCount.get(d.nr) ?? 0) > 0)
            .map(d => ({ label: t(STATUS_TEXT_KEYS[d.nr as TicketStatus]), value: statusCount.get(d.nr) ?? 0, color: STATUS_BADGE[d.nr as TicketStatus] }));

        const byPriority: Slice[] = [1, 2, 3]
            .filter(p => (prioCount.get(p) ?? 0) > 0)
            .map(p => ({ label: t(PRIORITY_TEXT_KEYS[p as TicketPriority]), value: prioCount.get(p) ?? 0, color: PRIORITY_BADGE[p as TicketPriority] }));

        const palette = ["#6366F1", "#0EA5E9", "#10B981", "#F97316", "#A855F7", "#EF4444", "#14B8A6", "#EAB308"];
        const byType: Slice[] = [...typeCount.entries()].sort((a, b) => b[1] - a[1]).map(([label, value], i) => ({ label, value, color: palette[i % palette.length] }));
        const byProduct: Slice[] = [...productCount.entries()].sort((a, b) => b[1] - a[1]).map(([label, value], i) => ({ label, value, color: palette[i % palette.length] }));

        const resolutionRate = total > 0 ? Math.round((closed / total) * 100) : 0;
        return { total, open, closed, resolutionRate, byStatus, byPriority, byType, byProduct };
    }, [tickets, t]);

    return (
        <div className="h-full w-full" style={{ overflowY: "auto", padding: 20, color: "var(--base-text-color)" }}>
            <LoadPanel visible={loading} />

            <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>{t("ticketAnalysis")}</h2>

            {/* Ozet kartlar */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" style={{ marginBottom: 20 }}>
                <Metric label={t("totalTickets")} value={data.total} color="#6366F1" />
                <Metric label={t("openTickets")} value={data.open} color="#F97316" />
                <Metric label={t("closedTickets")} value={data.closed} color="#10B981" />
                <Metric label={t("resolutionRate")} value={`%${data.resolutionRate}`} color="#0EA5E9" />
            </div>

            {/* Kirilimlar */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <BarCard title={t("byStatus")} slices={data.byStatus} />
                <BarCard title={t("byPriority")} slices={data.byPriority} />
                <BarCard title={t("byType")} slices={data.byType} />
                <BarCard title={t("byProduct")} slices={data.byProduct} />
            </div>
        </div>
    );
}

function MOCK_ANALYTICS(t: (k: string) => string) {
    const byStatus: Slice[] = [
        { label: t(STATUS_TEXT_KEYS[1 as TicketStatus]), value: 24, color: STATUS_BADGE[1] },
        { label: t(STATUS_TEXT_KEYS[5 as TicketStatus]), value: 13, color: STATUS_BADGE[5] },
        { label: t(STATUS_TEXT_KEYS[7 as TicketStatus]), value: 9, color: STATUS_BADGE[7] },
        { label: t(STATUS_TEXT_KEYS[9 as TicketStatus]), value: 48, color: STATUS_BADGE[9] },
    ];
    const byPriority: Slice[] = [
        { label: t("priorityHigh"), value: 18, color: PRIORITY_BADGE[1] },
        { label: t("priorityNormal"), value: 52, color: PRIORITY_BADGE[2] },
        { label: t("priorityLow"), value: 24, color: PRIORITY_BADGE[3] },
    ];
    const byType: Slice[] = [
        { label: "Destek", value: 41, color: "#6366F1" },
        { label: "Hata", value: 28, color: "#EF4444" },
        { label: "Özellik Talebi", value: 16, color: "#10B981" },
        { label: "Servis", value: 9, color: "#F97316" },
    ];
    const byProduct: Slice[] = [
        { label: "mobiOffice", value: 38, color: "#6366F1" },
        { label: "mobiSales", value: 27, color: "#0EA5E9" },
        { label: "mobiShop", value: 19, color: "#10B981" },
        { label: "mobiRoute", value: 10, color: "#F97316" },
    ];
    return { total: 94, open: 46, closed: 48, resolutionRate: 51, byStatus, byPriority, byType, byProduct };
}

function Metric({ label, value, color }: { label: string; value: string | number; color: string }) {
    return (
        <div style={{ background: "var(--base-bg)", border: "1px solid var(--border-color)", borderLeft: `4px solid ${color}`, borderRadius: 12, padding: "14px 16px" }}>
            <div style={{ fontSize: 28, fontWeight: 700, color }}>{value}</div>
            <div className="text-xs" style={{ color: "var(--subtitle-text-color)", marginTop: 2 }}>{label}</div>
        </div>
    );
}

function BarCard({ title, slices }: { title: string; slices: Slice[] }) {
    const max = Math.max(1, ...slices.map(s => s.value));
    const total = slices.reduce((s, x) => s + x.value, 0);
    return (
        <div style={{ background: "var(--base-bg)", border: "1px solid var(--border-color)", borderRadius: 12, padding: 16 }}>
            <div className="text-sm font-semibold uppercase" style={{ letterSpacing: "0.04em", color: "var(--subtitle-text-color)", marginBottom: 12 }}>
                {title}
            </div>
            {slices.length === 0 ? (
                <div className="text-sm" style={{ color: "var(--subtitle-text-color)" }}>—</div>
            ) : (
                <div className="flex flex-col gap-3">
                    {slices.map(s => {
                        const pct = total > 0 ? Math.round((s.value / total) * 100) : 0;
                        return (
                            <div key={s.label}>
                                <div className="flex items-center justify-between" style={{ marginBottom: 4 }}>
                                    <span className="text-sm flex items-center gap-2">
                                        <span style={{ width: 10, height: 10, borderRadius: 3, background: s.color, flexShrink: 0 }} />
                                        {s.label}
                                    </span>
                                    <span className="text-xs" style={{ color: "var(--subtitle-text-color)" }}>
                                        <strong style={{ color: "var(--base-text-color)" }}>{s.value}</strong> · %{pct}
                                    </span>
                                </div>
                                <div style={{ height: 8, borderRadius: 999, background: "var(--base-bg-darken-4)", overflow: "hidden" }}>
                                    <div style={{ width: `${(s.value / max) * 100}%`, height: "100%", background: s.color }} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
